import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import "flag-icons/css/flag-icons.min.css";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { usePublicAuth } from "../context/PublicAuthContext";

const LANGUAGES = [
  { code: "de", fi: "de", label: "Deutsch" },
  { code: "en", fi: "gb", label: "English" },
  { code: "pl", fi: "pl", label: "Polski" },
];

/**
 * LanguageSwitcher – Cookie-basiert, optional DB-Speicherung wenn eingeloggt.
 * Props:
 *   variant: "dropdown" (default) | "inline"
 */
export default function LanguageSwitcher({ variant = "dropdown" }) {
  const { i18n } = useTranslation();
  const { publicApi, isPublicAuth } = usePublicAuth();

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const changeLanguage = useCallback(async (code) => {
    await i18n.changeLanguage(code);
    // Persist to DB if logged in
    if (isPublicAuth) {
      try {
        await publicApi("put", "/me/language", { language: code });
      } catch { /* silent */ }
    }
  }, [i18n, isPublicAuth, publicApi]);

  if (variant === "inline") {
    return (
      <div className="flex items-center gap-1.5" data-testid="lang-switcher-inline">
        {LANGUAGES.map(lang => (
          <button
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            data-testid={`lang-btn-${lang.code}`}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              i18n.language === lang.code
                ? "bg-white/10 text-white border border-white/20"
                : "text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.05] border border-transparent"
            }`}
          >
            <span className={`fi fi-${lang.fi} rounded-sm`} style={{ fontSize: "13px" }} />
            <span className="hidden sm:inline">{lang.code.toUpperCase()}</span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="lang-switcher-btn"
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.05] transition-all text-xs font-medium border border-transparent hover:border-white/[0.08] focus:outline-none"
          aria-label="Language / Sprache"
        >
          <Globe size={14} />
          <span className={`fi fi-${currentLang.fi} rounded-sm`} style={{ fontSize: "13px" }} />
          <span className="hidden sm:inline">{currentLang.code.toUpperCase()}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-36 rounded-xl bg-zinc-950/95 backdrop-blur-xl border border-white/10 shadow-[0_16px_48px_rgba(0,0,0,0.6)] p-1 animate-in fade-in-0 zoom-in-95 duration-150"
      >
        {LANGUAGES.map(lang => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            data-testid={`lang-option-${lang.code}`}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors ${
              i18n.language === lang.code
                ? "text-white bg-white/[0.08]"
                : "text-zinc-400 hover:text-white hover:bg-white/[0.06] focus:bg-white/[0.06]"
            }`}
          >
            <span className={`fi fi-${lang.fi} rounded-sm flex-shrink-0`} style={{ fontSize: "15px" }} />
            <span>{lang.label}</span>
            {i18n.language === lang.code && (
              <span className="ml-auto w-1.5 h-1.5 rounded-full bg-rose-500" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
